import { ChangeEvent, FC, FormEvent, useState } from 'react'
import { InputFile } from './input-file'
import Loading from '../../../components/Loading'
import { DocumentsType } from './interface'

interface IdentificationDocumentProps {
    handleSubmit(document: DocumentsType): Promise<{ error: boolean }>
    setIsOpen(value: boolean): void
}

export const IdentificationDocument: FC<IdentificationDocumentProps> = ({
    handleSubmit,
    setIsOpen,
}) => {
    const [documents, setDocuments] = useState<DocumentsType>({
        documentGroup: 'documento-identificacao',
        files: [],
        numFilesRequired: 1,
    })
    const [documentSent, setDocumentSent] = useState<'cnh' | 'outro'>('cnh')
    const [isLoading, setIsLoading] = useState(false)

    const onSubmit = async (e: FormEvent) => {
        e.preventDefault()
        setIsLoading(true)
        const result = await handleSubmit(documents)
        setIsLoading(false)

        if (result.error === false) {
            setIsOpen(false)
        }
    }

    const handleDocTypeChange = (value: 'cnh' | 'outro') => {
        setDocumentSent(value)
        setDocuments((prevState) => {
            return {
                ...prevState,
                files: [],
                numFilesRequired: value === 'cnh' ? 1 : 3,
            }
        })
    }

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]

        if (!file) return

        const newDocument = {
            docType: event.target.name, // Use o atributo 'name' para armazenar o tipo do documento
            file,
        }

        const filesFiltred = documents.files.filter(
            (item) => item.docType !== newDocument.docType
        )

        // const numFilesRequired = newDocument.docType === 'cnh' ? 1 : 3

        setDocuments((prevState) => ({
            ...prevState,
            files: [...filesFiltred, newDocument],
        }))
    }

    const cnhDocument = documents.files.find(
        (document) => document.docType === 'cnh'
    )

    const frontIdentificationDocument = documents.files.find(
        (document) => document.docType === 'frente-documento-identificacao'
    )

    const backIdentificationDocument = documents.files.find(
        (document) => document.docType === 'verso-documento-identificacao'
    )

    const cpfDocument = documents.files.find(
        (document) => document.docType === 'cpf'
    )

    if (isLoading) {
        return (
            <div className="flex items-center justify-center w-full">
                <Loading />
            </div>
        )
    }

    return (
        <form
            onSubmit={onSubmit}
            className="flex flex-col justify-between gap-4 mt-6 w-full"
        >
            <div className="overflow-auto flex-1 px-6 max-h-[50vh]">
                <div className="mb-8 flex flex-col gap-1">
                    <label htmlFor="tipo-documento">
                        Qual documento será enviado?{' '}
                        <span className="text-red-500">*</span>
                    </label>
                    <select
                        id="tipo-documento"
                        // value={credenciadora}
                        name="tipo-documento"
                        placeholder="Selecione"
                        value={documentSent}
                        onChange={(e) =>
                            handleDocTypeChange(e.target.value as any)
                        }
                        className="cursor-pointer rounded-lg w-full flex-1 border border-gray-300 py-1 px-2 bg-white text-gray-700 placeholder-gray-400 shadow-sm text-base focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    >
                        {/* <option disabled>Selecione</option> */}
                        <option value="cnh">CNH (Habilitação)</option>
                        <option value="outro">
                            Outro documento de identificação
                        </option>
                    </select>
                </div>
                <div>
                    {documentSent === 'cnh' && (
                        <InputFile
                            label="CNH"
                            document={cnhDocument}
                            handleFileChange={handleFileChange}
                            name="cnh"
                        />
                    )}
                    {documentSent === 'outro' && (
                        <div className="flex flex-col gap-2">
                            <InputFile
                                label="Frente do documento de identificação"
                                document={frontIdentificationDocument}
                                handleFileChange={handleFileChange}
                                name="frente-documento-identificacao"
                            />
                            <InputFile
                                label="Verso do documento de identificação"
                                document={backIdentificationDocument}
                                handleFileChange={handleFileChange}
                                name="verso-documento-identificacao"
                            />
                            <InputFile
                                label="CPF"
                                document={cpfDocument}
                                handleFileChange={handleFileChange}
                                name="cpf"
                            />
                        </div>
                    )}
                </div>
                <div className="flex flex-col gap-8 mt-8">
                    <div className="bg-amber-300/80 rounded-md p-4 shadow-sm">
                        <ul className="list-disc list-inside tracking-tight">
                            {documentSent === 'cnh' ? (
                                <>
                                    <li>
                                        A CNH deve estar{' '}
                                        <span className="text-amber-800 font-semibold">
                                            aberta
                                        </span>
                                        , em boa qualidade, boa iluminação e com
                                        dados legíveis.
                                    </li>
                                    <li>
                                        Você deve enviar uma foto da{' '}
                                        <span className="text-amber-800 font-semibold">
                                            CNH original
                                        </span>{' '}
                                        ou a{' '}
                                        <span className="text-amber-800 font-semibold">
                                            CNH digital
                                        </span>
                                        .
                                    </li>
                                </>
                            ) : (
                                <>
                                    <li>
                                        Os documentos devem estar em boa
                                        qualidade, boa iluminação e com dados
                                        legíveis.
                                    </li>{' '}
                                    <li>
                                        Você deve enviar uma foto do{' '}
                                        <span className="text-amber-800 font-semibold">
                                            documento original
                                        </span>
                                        .
                                    </li>{' '}
                                    <li>
                                        Para o campo{' '}
                                        <span className="text-amber-800 font-semibold">
                                            CPF
                                        </span>{' '}
                                        você pode repetir o documento de
                                        identificação, desde que conste o CPF
                                        nele. Caso não conste você pode{' '}
                                        <a
                                            href="https://www.google.com/search?q=emitir+2+via+cpf"
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-blue-600 underline"
                                        >
                                            emitir 2a via
                                        </a>
                                        .
                                    </li>
                                    <li>
                                        Exemplos de documentos de identificação:
                                        RG e Carteira de Trabalho.
                                    </li>
                                </>
                            )}
                        </ul>
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-end gap-2 pb-6 px-6">
                <button
                    type="button"
                    onClick={() => setIsOpen(false)}
                    className="border border-black py-1 px-2 rounded-md h-8 flex items-center justify-center hover:bg-gray-400/20 transition ease-in duration-100 focus:ring-red-500 focus:ring-offset-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2"
                >
                    Cancelar
                </button>
                <button
                    type="submit"
                    className="py-1 px-2 flex items-center justify-center gap-x-1 bg-green-600 hover:opacity-90 focus:ring-green-500 focus:ring-offset-green-200 text-white transition ease-in duration-100 text-center text-base font-normal shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 rounded-md h-8"
                >
                    Confirmar
                </button>
            </div>
        </form>
    )
}
